import cx from 'classnames';
import {map, isEmpty, get, omit} from 'lodash';
import {Fragment, useCallback} from 'react';

import {Checkbox, CheckboxProps} from 'components';
import {MARGIN_MAP} from 'constants/margin-map';
import {FormattedPermission, UserPermissions} from 'types';

type Props = {
    selectedPermissions?: UserPermissions;
    availablePermissions?: UserPermissions;
    isParentIntermediate?: (path: string, permissions: UserPermissions) => boolean;
    isParentSelected?: (path: string, permissions: UserPermissions) => boolean;
    isItemSelected?: (path: string, permissions: UserPermissions) => boolean;
    onToggleItem?: (path: string, isParent?: boolean) => void;
    formattedPermissions: FormattedPermission[];
};

export const EditablePermissionList: React.FC<Props> = ({
    selectedPermissions, availablePermissions, formattedPermissions,
    isParentIntermediate, isParentSelected, isItemSelected, onToggleItem
}: Props) => {
    const getSelectedKeys = useCallback((permissions) => map(permissions, (permission) => {
        if (permission.children) {
            return getSelectedKeys(permission.children);
        } else {
            return permission.path;
        }
    }), []);

    const renderCheckbox = useCallback((path: string, label: string) => {
        const isParent = !isEmpty(omit(get(availablePermissions, path), 'enabled'));
        const checkboxProps: CheckboxProps = {
            label,
            labelPlacement: 'right',
            name: path,
            fullWidth: true,
            ...(isParent && {indeterminate: isParentIntermediate?.(path, selectedPermissions)}),
            checked: isParent ? isParentSelected(path, selectedPermissions) : isItemSelected(path, selectedPermissions),
            onChange: () => {
                onToggleItem?.(path, isParent);
            }
        };
        return <Checkbox {...checkboxProps} />;
    }, [availablePermissions, isItemSelected, isParentIntermediate, isParentSelected, onToggleItem, selectedPermissions]);

    const isPermissionRoot = useCallback((path: string) => !path.includes('.'), []);

    const renderPermissionList = useCallback((permissions: FormattedPermission[], depth = 1) => (
        <div>
            {map(permissions, ({path, children, label}) => (
                <Fragment key={path}>
                    <div
                        className={cx('tw-my-1 tw-p-1 tw-w-full', {
                            'tw-font-bold': isPermissionRoot(path)
                        })}
                    >
                        {renderCheckbox(path, label)}
                    </div>
                    {!isEmpty(children) &&
                        <div className={MARGIN_MAP[depth]}>
                            {renderPermissionList(children, depth + 1)}
                        </div>}
                </Fragment>
            ))}
        </div>
    ), [isPermissionRoot, renderCheckbox]);

    return (
        <div className='permission-list'>
            {renderPermissionList(formattedPermissions)}
        </div>
    );
};